/**
 * Unified number formatting utilities
 */
import React from 'react';

/**
 * Format money with Unicode minus before $: "$1,234" or "\u22121,234"
 * Uses Intl for proper thousand separators.
 */
export function formatMoney(value: number | null | undefined): string {
  if (value == null) return '-';
  const abs = Math.abs(value);
  const formatted = `$${abs.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  return value < 0 ? `\u2212${formatted}` : formatted;
}

/**
 * Compact money: "$1.2k" or "\u2212$1.2k" for large values, "$123" for small.
 * Unicode minus before dollar sign for negatives.
 */
export function formatMoneyCompact(value: number | null | undefined): string {
  if (value == null) return '-';
  const abs = Math.abs(value);
  let formatted: string;
  if (abs >= 1000) {
    formatted = `$${(abs / 1000).toFixed(1)}k`;
  } else {
    formatted = `$${abs.toFixed(0)}`;
  }
  return value < 0 ? `\u2212${formatted}` : formatted;
}

/**
 * Parse numeric value from string with support for both dot and comma as decimal separators.
 * Limits to 3 decimal places max.
 * Useful for InputNumber parser prop.
 */
export function parseNumericValue(value: string | undefined): number {
  if (!value) return 0;
  // Replace comma with dot for parsing (support both decimal separators)
  const cleaned = value.replace(/,/g, '.').replace(/[^\d.-]/g, '');
  const num = parseFloat(cleaned);
  if (isNaN(num)) return 0;
  // Round to 3 decimal places max
  return Math.round(num * 1000) / 1000;
}

/**
 * Allowed keys for numeric input validation.
 * Includes both dot and comma as decimal separators.
 */
export const numericAllowedKeys = [
  'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
  'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
  'Home', 'End', '.', ',', '-',
];

/**
 * KeyDown handler for numeric inputs that blocks non-numeric keys.
 * Supports both dot and comma as decimal separators.
 */
export const handleNumericKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
  // Allow all Ctrl/Cmd shortcuts (copy, paste, undo, redo, select all, etc.)
  if (e.ctrlKey || e.metaKey) {
    return;
  }

  const isNumber = /^[0-9]$/.test(e.key);
  const isAllowed = numericAllowedKeys.includes(e.key);

  if (!isNumber && !isAllowed) {
    e.preventDefault();
  }
};

/**
 * Common props for numeric InputNumber components.
 * Use spread operator: {...numericInputProps}
 */
export const numericInputProps = {
  onKeyDown: handleNumericKeyDown,
  parser: parseNumericValue,
};
